/**
 * rateFlag.js — whether /rate is OFFERED anywhere.
 *
 * 🔴 DEFAULT ON SINCE SEP 4 2026: /rate IS LAUNCHED. It was gated while it was
 * finished enough for Ben to run his own team and his own league through and not
 * yet something a visitor should stumble into. That stealth period is over, so
 * both discovery surfaces ship to everyone. This is liveBoardFlag.js's mechanism
 * and now also its direction: `?rate=0` turns discovery off for a browser and
 * persists that choice, `?rate=1` turns it back on. The stored key therefore
 * means "explicitly off" — the same meaning the live board's key carries.
 *
 * 🔴 THE MODULE STAYS, AND SO DOES ITS OFF SWITCH. `?rate=0` is the way to pull
 * the feature from a browser without a deploy. `?rate=1` still works and is now
 * a no-op on a stock profile.
 *
 * 🔴 A STALE '1' FROM THE STEALTH PERIOD READS AS ON, unchanged — it is not '0',
 * and only '0' means anything now. Nobody who opted in during stealth gets
 * flipped off by this launch.
 *
 * 🔴 IT GATES DISCOVERY AND NOTHING ELSE. Two surfaces read it:
 *   - the RATE MY AUCTION card on the landing page, and
 *   - the "Get your whole room graded" cross-link on /submit.
 *
 * 🔴 THE /rate ROUTE ITSELF IS NEVER GATED, and never was. App.js matches the
 * pathname before any of this is read, so a direct link works for anybody who
 * has one whatever the flag says. Nothing in this module is imported by App.js
 * or by RatePage, and a test asserts /rate renders with storage completely
 * empty.
 *
 * ⚠️ LAUNCH WAS NOT A ONE-LINE COMMIT, whatever this header used to claim.
 * BOTH halves had to move together: flipping only read() to `!== '0'` would
 * have left write(false) REMOVING the key, and an absent key reads as ON under
 * the new rule — so `?rate=0` would have silently stopped working, which is the
 * one behaviour the off switch exists to provide. read() and write() encode the
 * same convention and must always be changed as a pair.
 */
export const RATE_FLAG_KEY = 'auctionArchitect_rateEnabled';

// '0' is the only stored value that means anything now: explicitly opted out.
// Anything else — absent, or the '1' a stealth-period profile still carries — is
// on.
function read() {
  try {
    return localStorage.getItem(RATE_FLAG_KEY) !== '0';
  } catch {
    return true;                      // private mode / storage disabled: the
                                      // feature is launched, so it stays offered
  }
}

function write(on) {
  try {
    if (on) localStorage.removeItem(RATE_FLAG_KEY);
    else localStorage.setItem(RATE_FLAG_KEY, '0');
  } catch { /* nothing to do — the choice simply does not persist */ }
}

/**
 * Apply `?rate=` if present, then report the resulting state.
 *
 * Call once, at surface start. Any value other than "1" or "0" is ignored rather
 * than treated as off, so a stray `?rate=` cannot silently pull the feature for
 * someone — the same rule liveBoardFlag applies, now in the same direction.
 */
export function resolveRateFlag(search) {
  let param = null;
  try {
    const qs = search != null ? search : (typeof window !== 'undefined' ? window.location.search : '');
    param = new URLSearchParams(qs).get('rate');
  } catch { /* no URL API / malformed query — fall through to stored state */ }

  if (param === '1') write(true);
  else if (param === '0') write(false);
  return read();
}

/** The stored state, without touching the URL. */
export function isRateEnabled() {
  return read();
}
